import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { IonChip, IonLabel } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { TagChip } from './TagChip';
import { LISTED_TAG_CHIP } from './TagChipMetrics';
import { TagChipWidths } from './TagChipWidths';
import { TagRowFitter } from './TagRowFitter';
import { Logger } from '../support/Logger';

const tagGap = 4;

const useStyles = createUseStyles({
    row: {
        // as wide as its container whatever chips it holds, so a container that sizes to its
        // content (an appointment's details) cannot widen to fit chips the row should leave out
        width: 0,
        minWidth: '100%',
        display: 'flex',
        flexWrap: 'nowrap',
        gap: tagGap,
        marginTop: 4,
        marginBottom: 4,
        overflow: 'hidden'
    },

    leading: {
        display: 'flex',
        flexShrink: 0
    },

    moreChip: {
        flexShrink: 0,
        paddingLeft: LISTED_TAG_CHIP.paddingInline,
        paddingRight: LISTED_TAG_CHIP.paddingInline,
        marginLeft: LISTED_TAG_CHIP.marginInline,
        marginRight: LISTED_TAG_CHIP.marginInline,
        backgroundColor: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-600)'
    },

    label: {
        fontSize: 12,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    }
});

// Counts how many tags fit the row from their labels' text widths: first before the row paints, then
// again when the row or the leading chip resizes, and once the label font has loaded.
const useTagsThatFit = ({ tags, hasLeadingChip }: { tags: string[]; hasLeadingChip: boolean; }) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const leadingRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(tags.length);

    const fitter = useInstance(TagRowFitter);
    const chipWidths = useInstance(TagChipWidths);
    const logger = useInstance(Logger);

    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) {
            return;
        }

        let rowWidth = row.getBoundingClientRect().width;
        let leadingWidth = leadingRef.current?.getBoundingClientRect().width;

        const fit = () => {
            // the label's own font, which is what its text is laid out in. Every tag renders until the
            // first fit and the first tag always after, so there is a label whenever there are tags.
            const label = row.querySelector('ion-chip[data-tag-kind="listed"] ion-label');
            if (!label) {
                return;
            }
            const font = getComputedStyle(label);
            setVisibleCount(fitter.countTagsThatFit({
                rowWidth,
                leadingWidth,
                tagWidths: tags.map(tag => chipWidths.listedTag({ tag, font })),
                // the widest "+N more" the row could need
                moreChipWidth: chipWidths.moreChip({ label: `+${tags.length} more`, font }),
                gap: tagGap
            }));
        };

        fit();

        const stopRefitting = refitOnceFontsLoad({ fit, onError: err => logger.error(err, { message: 'OneLineTagChips failed to fit its tags' }) });
        const stopObserving = observeWidths({
            row,
            leading: leadingRef.current,
            onResize: widths => {
                rowWidth = widths.row ?? rowWidth;
                leadingWidth = widths.leading ?? leadingWidth;
                fit();
            }
        });
        return () => {
            stopRefitting();
            stopObserving();
        };
    }, [hasLeadingChip]); // eslint-disable-line react-hooks/exhaustive-deps

    return { rowRef, leadingRef, visibleCount };
};

// A fit made in a fallback font counts the wrong tags, so fit again in the real one. Returns its cleanup.
const refitOnceFontsLoad = ({ fit, onError }: { fit: () => void; onError: (err: unknown) => void; }): (() => void) => {
    if (document.fonts.status === 'loaded') {
        return () => {};
    }
    let isDisposed = false;
    const refit = async () => {
        await document.fonts.ready;
        if (!isDisposed) {
            fit();
        }
    };
    refit().catch(onError);
    return () => {
        isDisposed = true;
    };
};

// Later sizes come from a ResizeObserver, so a resize forces no layout. Returns its cleanup.
const observeWidths = ({ row, leading, onResize }: {
    row: Element;
    leading: Element | null;
    onResize: (widths: { row?: number; leading?: number; }) => void;
}): (() => void) => {
    const observer = new ResizeObserver(entries => {
        const widths: { row?: number; leading?: number; } = {};
        for (const entry of entries) {
            if (entry.target === row) {
                widths.row = entry.contentRect.width;
            } else {
                widths.leading = entry.contentRect.width;
            }
        }
        onResize(widths);
    });
    observer.observe(row);
    if (leading) {
        observer.observe(leading);
    }
    return () => observer.disconnect();
};

type IOneLineTagChipsProps = {
    tags?: string[];
    // a chip shown before the tags that is never left out, such as a marker for where the item came from
    leadingChip?: ReactNode;
};

const OneLineTagChipsRow: FC<IOneLineTagChipsProps & { tags: string[]; }> = ({ tags, leadingChip }) => {
    const classes = useStyles();
    const { rowRef, leadingRef, visibleCount } = useTagsThatFit({ tags, hasLeadingChip: Boolean(leadingChip) });

    const visibleTags = tags.slice(0, visibleCount);
    const hiddenCount = tags.length - visibleTags.length;

    return (
        <div ref={rowRef} className={classes.row} data-type="tag-chips">
            {leadingChip && (
                <div ref={leadingRef} className={classes.leading}>
                    {leadingChip}
                </div>
            )}

            {/* the first tag always shows, so a tag too long for the row shrinks to an ellipsis */}
            {visibleTags.map((tag, index) => (
                <TagChip key={tag} kind="listed" tag={tag} shrinksToEllipsis={index === 0} />
            ))}
            {hiddenCount > 0 && (
                <IonChip className={classes.moreChip} data-type="tag-chips-more">
                    <IonLabel className={classes.label}>+{hiddenCount} more</IonLabel>
                </IonChip>
            )}
        </div>
    );
};

// A list item's tags on one line: as many whole chips as fit, then "+N more" for the rest.
export const OneLineTagChips: FC<IOneLineTagChipsProps> = ({ tags = [], leadingChip }) => {
    if (tags.length === 0 && !leadingChip) {
        return null;
    }

    // keyed on the tags, so a change of tags starts again from showing them all
    return <OneLineTagChipsRow key={tags.join('\n')} tags={tags} leadingChip={leadingChip} />;
};
